import { dash } from "@dropbox/api-v2-client";
import { callCompanyOSEmployeeApi } from "@mirage/mosaics/BaseProfilePage/companyos-apis";
import { useCompanyOSIntegrationVariant } from "@mirage/mosaics/BaseProfilePage/utils";
import { userInfoAtom } from "@mirage/mosaics/UserProfilePage/atoms";
import { callApiV2 } from "@mirage/service-dbx-api";
import { useAtom } from "jotai";
import { useEffect } from "react";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";

export const formatDashGetPersonResponse = (
  response: dash.GetPersonResponse,
): UserInfo | undefined => {
  const personResponse = response?.person;
  if (
    !personResponse ||
    !personResponse.user_id ||
    !personResponse.email ||
    !personResponse.name
  ) {
    return undefined;
  }

  return {
    user_id: personResponse.user_id,
    name: personResponse.name,
    email: personResponse.email,
    profile_photo_url: personResponse.profile_photo_url ?? null,
    role: personResponse.role ?? null,
    level: personResponse.level ?? null,
    is_deactivated: personResponse.is_deactivated ?? false,
    tenure: personResponse.tenure_undefined
      ? null
      : (personResponse.tenure ?? null),
    tenure_percentile: personResponse.tenure_percentile_undefined
      ? null
      : (personResponse.tenure_percentile ?? null),
    birthday: personResponse.birthday ?? null,
    city: personResponse.city ?? null,
    hire_date: personResponse.hire_date ?? null,
    location: personResponse.location ?? null,
    manager_id: personResponse.manager_id ?? null,
    on_leave: personResponse.on_leave ?? false,
    show_birthday: false,
    timezone_offset: personResponse.timezone_offset_undefined
      ? null
      : (personResponse.timezone_offset ?? null),
    type: "",
    upcoming_pto_list: personResponse.upcoming_pto?.length
      ? personResponse.upcoming_pto.map((pto) => ({
          start_date: pto.start_date ?? null,
          end_date: pto.end_date ?? null,
        }))
      : null,
    teams: [],
    team_names: personResponse.teams ?? [],
    total_report_count: personResponse.direct_reports?.length ?? 0,
    reporting_line: [],
    peers: [],
    reports: [],
    about_me: personResponse.about_me ?? null,
  };
};

export function useUserProfileApi({ userId }: { userId: string }) {
  const [userInfo, setUserInfo] = useAtom(userInfoAtom);
  const variant = useCompanyOSIntegrationVariant();

  useEffect(() => {
    async function fetchUserInfo() {
      if (variant === "ON") {
        callCompanyOSEmployeeApi({ userId }).then((response) => {
          if (response) {
            setUserInfo(response);
          }
        });
      } else if (variant === "V1") {
        callApiV2("dashPeopleGetPerson", {
          user_id: userId,
        }).then((response) => {
          const formattedResponse = formatDashGetPersonResponse(response);
          if (formattedResponse) {
            setUserInfo(formattedResponse);
          }
        });
      }
    }

    fetchUserInfo();
  }, [userId]);

  return userInfo;
}
